import React, { useEffect, useState } from 'react';
import Axios from 'axios';
import authConfig from '../../authConfig';
import { Table, Spinner, Button } from 'react-bootstrap';

/**
 * MemberAttendance component
 * Displays attendance information for a member.
 * Props:
 * - member: { id: string, name: string, attendance: number }
 */
const MemberAttendance = ({ memberId, from, to, withbreakdown = true }) => {
    const [attendanceData, setAttendanceData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showCommittees, setShowCommittees] = useState(false);

    useEffect(() => {
        if (!memberId) return;
        setLoading(true);
        Axios.get(authConfig.api.urls.getMemberAttendance, {
            params: { id: memberId, from, to }
        })
            .then(res => {
                setAttendanceData(res.data);
                setLoading(false);
            })
            .catch(() => setLoading(false));

    }, [memberId, from, to]);


    if (!memberId) {
        return <div>No member data available.</div>;
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '120px' }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading attendance...</span>
                </Spinner>
            </div>
        );
    }

    const attendanceTypes = attendanceData ? Object.keys(attendanceData.totals || {}) : {};
    const committeeIds = attendanceData ? Object.keys(attendanceData.totalsByCommittee || {}) : {};
    const allTypes = Object.keys(attendanceData.totals || {});
    // Filter attendance types: only show columns where at least one value is non-zero
    const visibleTypes = allTypes.filter(type => {
        // Check totals
        if (attendanceData.totals[type] && attendanceData.totals[type] !== 0) return true;
        // Check each committee
        for (const cid of committeeIds) {
            const committeeAttendance = attendanceData.totalsByCommittee[cid];
            if (committeeAttendance && committeeAttendance[type] && committeeAttendance[type] !== 0) {
                return true;
            }
        }
        return false;
    });
    // Calculate total for all visible types
    const totalForAllTypes = visibleTypes.reduce((sum, type) => sum + (attendanceData.totals[type] || 0), 0);

    // Calculate percentage for each type
    const percentageRow = visibleTypes.map(type => {
        const value = attendanceData.totals[type] || 0;
        if (totalForAllTypes === 0) return '0%';
        return ((value / totalForAllTypes) * 100).toFixed(1) + '%';
    });
    const fromformatted = (new Date(from)).toDateString();
    const toformatted = (new Date(to)).toDateString();
    return attendanceData == null ? (<>????</>) : (
        <div className="member-attendance">
            <b>Attendance for period {fromformatted} to {toformatted} </b>
            <Table striped bordered hover size="sm">
                <thead>
                    <tr>
                        {visibleTypes.map(type => (
                            <th key={type}>{type}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        {visibleTypes.map(type => (
                            <td key={type}>{attendanceData.totals[type]}</td>
                        ))}
                    </tr>
                    <tr>
                        {percentageRow.map((percent, idx) => (
                            <td key={visibleTypes[idx]}>{percent}</td>
                        ))}
                    </tr>
                </tbody>
            </Table>
            {withbreakdown ? (<></>) : (
                <Button
                    variant="success"
                    size="sm"
                    className="mb-3"
                    onClick={() => setShowCommittees(v => !v)}
                >
                    {showCommittees ? 'Hide Committee Breakdown' : 'Show Committee Breakdown'}
                </Button>
            )}
            {(showCommittees || withbreakdown) && (
                <>
                <br/>
                    <b>Attendance by Committee</b>
                    <Table striped bordered hover size="sm">
                        <thead>
                            <tr>
                                <th>Committee</th>
                                {visibleTypes.map(type => (
                                    <th key={type}>{type}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {committeeIds.map(cid => {
                                const committeeAttendance = attendanceData.totalsByCommittee[cid];
                                const committeeName = attendanceData.committeeNames[cid];
                                return (
                                    <tr key={cid}>
                                        <td>{committeeName.en}</td>
                                        {visibleTypes.map(type => (
                                            <td key={type}>
                                                {committeeAttendance[type] || 0}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </>
            )}
        </div>
    );
};

export default MemberAttendance;